import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';

import { configureRequests } from '@client/lib/requests';

const RequestsAuth0Bridge = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect, user } = useAuth0();

  useEffect(() => {
    configureRequests({
      getAccessToken: async (audience?: string) => {
        if (!isAuthenticated || isLoading) {
          // eslint-disable-next-line no-console
          console.warn('[auth0-bridge] getAccessToken called before auth ready', {
            audience,
            isAuthenticated,
            isLoading,
          });
          return undefined;
        }

        // Passing `authorizationParams.audience` triggers Auth0 to mint
        // (and cache) a separate token whose `aud` matches that API.
        // No audience arg = the default audience configured at SDK init.
        if (audience) {
          try {
            return await getAccessTokenSilently({ authorizationParams: { audience } });
          } catch (err) {
            // Audience-specific fetch failed — most commonly because the
            // Auth0 SPA app isn't authorized for that API yet, or the
            // PostLogin action redirects (incompatible with prompt=none).
            // eslint-disable-next-line no-console
            console.warn('[auth0-bridge] audience-specific token failed, falling back', {
              audience,
              error: (err as Error)?.message,
            });
          }
        }

        try {
          return await getAccessTokenSilently();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[auth0-bridge] default-audience token failed', {
            error: (err as Error)?.message,
          });
          return undefined;
        }
      },
      getAuth: () => ({
        email: user?.email,
      }),
      getRuntime: () => ({
        environment: import.meta.env.VITE_NODE_ENV ?? import.meta.env.MODE,
        analyticsServerURL: import.meta.env.VITE_ANALYTICS_SERVER_URL,
        licenseAndUpdateBaseUrl: import.meta.env.VITE_LICENSE_AND_UPDATE_BASE_URL,
        cortexStudioUrl: import.meta.env.VITE_CORTEX_STUDIO_URL,
      }),
      onAuthFailure: () => {
        loginWithRedirect();
      },
    });
  }, [getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect, user?.email]);

  return null;
};

export default RequestsAuth0Bridge;
